import { trackingQueryParameters } from '@vendepay/contracts';
import type { CheckoutUtmAttribution } from './attribution';
import type { PublicCheckout, PublicCheckoutTrackingPixel } from './contract';
import type { PurchasePixelValue } from './api';

export interface MetaPixelStub {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  push: (...args: unknown[]) => void;
  loaded: boolean;
  version: string;
  queue: IArguments[];
}

export interface TikTokQueue extends Array<unknown[]> {
  methods?: string[];
  setAndDefer?: (target: Record<string, unknown>, method: string) => void;
  instance?: (pixelId: string) => Record<string, unknown>;
  load?: (pixelId: string, options?: Record<string, unknown>) => void;
  page?: () => void;
  track?: (eventName: string, properties?: Record<string, unknown>, options?: Record<string, unknown>) => void;
  _i?: Record<string, unknown[] & { _u?: string }>;
  _t?: Record<string, number>;
  _o?: Record<string, unknown>;
}

export type GtagFunction = (...args: unknown[]) => void;

declare global {
  interface Window {
    fbq?: MetaPixelStub;
    _fbq?: unknown;
    TiktokAnalyticsObject?: string;
    ttq?: TikTokQueue;
    dataLayer?: unknown[];
    gtag?: GtagFunction;
  }
}

const TIKTOK_METHODS = [
  'page',
  'track',
  'identify',
  'instances',
  'debug',
  'on',
  'off',
  'once',
  'ready',
  'alias',
  'group',
  'enableCookie',
  'disableCookie',
  'holdConsent',
  'revokeConsent',
  'grantConsent',
];

const TIKTOK_EVENTS_SRC = 'https://analytics.tiktok.com/i18n/pixel/events.js';
const SESSION_PREFIX = 'vendepay:checkout-tracking:';

const installedPixels = new Set<string>();
const firedEvents = new Set<string>();
const loadingScripts = new Set<Promise<void>>();

export async function waitForTrackingScripts(): Promise<void> {
  await Promise.all(loadingScripts);
}

export function resetCheckoutTracking(): void {
  installedPixels.clear();
  firedEvents.clear();
  loadingScripts.clear();
}

function experimentCustomData(checkout: PublicCheckout): Record<string, string> {
  if (checkout.experiment === undefined) {
    return {};
  }
  return {
    experiment_id: checkout.experiment.id,
    experiment_variant: checkout.experiment.variant,
  };
}

function checkoutEventData(checkout: PublicCheckout, utm?: CheckoutUtmAttribution): Record<string, string> {
  return { ...trackingQueryParameters(utm), ...experimentCustomData(checkout) };
}

export function installCheckoutPixels(checkout: PublicCheckout, utm?: CheckoutUtmAttribution): void {
  installMetaPixels(checkout, utm);
  installTikTokPixels(checkout, utm);
  installGooglePixels(checkout, utm);
}

export function trackAddPaymentInfo(checkout: PublicCheckout, utm?: CheckoutUtmAttribution): void {
  const eventId = `checkout:${checkout.checkoutId}:AddPaymentInfo`;
  if (alreadyFired(eventId)) {
    return;
  }
  const metaPixels = pixelsByProvider(checkout, 'facebook');
  const tiktokPixels = pixelsByProvider(checkout, 'tiktok');
  if (metaPixels.length === 0 && tiktokPixels.length === 0) {
    return;
  }
  markFired(eventId);
  if (metaPixels.length > 0) {
    window.fbq?.('track', 'AddPaymentInfo', checkoutEventData(checkout, utm), { eventID: eventId });
  }
  if (tiktokPixels.length > 0) {
    window.ttq?.track?.('AddPaymentInfo', checkoutEventData(checkout, utm), { event_id: eventId });
  }
}

export function trackPixGenerated(checkout: PublicCheckout, utm?: CheckoutUtmAttribution): void {
  const eventId = `checkout:${checkout.checkoutId}:GeneratePix`;
  if (alreadyFired(eventId)) {
    return;
  }
  const metaPixels = pixelsByProvider(checkout, 'facebook');
  if (metaPixels.length === 0) {
    return;
  }
  markFired(eventId);
  window.fbq?.('trackCustom', 'GeneratePix', checkoutEventData(checkout, utm), { eventID: eventId });
}

export function trackPurchaseValues(saleId: string, pixels: readonly PurchasePixelValue[], utm?: CheckoutUtmAttribution): void {
  const eventId = `purchase:${saleId}`;
  for (const pixel of pixels) {
    const value = Number(pixel.value);
    if (!Number.isFinite(value) || value < 0 || pixel.pixelId.trim().length === 0) continue;
    const key = `${eventId}:${pixel.provider}:${pixel.pixelId}:${pixel.conversionLabel}`;
    if (alreadyFired(key, true)) continue;
    const installKey = `${pixel.provider}:${pixel.pixelId}`;

    if (pixel.provider === 'facebook') {
      ensureMetaBase();
      if (!installedPixels.has(installKey)) window.fbq?.('init', pixel.pixelId);
      window.fbq?.(
        'trackSingle', pixel.pixelId, 'Purchase',
        { ...trackingQueryParameters(utm), value, currency: pixel.currency },
        { eventID: eventId },
      );
    } else if (pixel.provider === 'tiktok') {
      ensureTikTokBase();
      if (!installedPixels.has(installKey)) window.ttq?.load?.(pixel.pixelId);
      const instance = window.ttq?.instance?.(pixel.pixelId);
      const track = instance?.track;
      if (typeof track !== 'function') continue;
      track.call(instance, 'Purchase', { ...trackingQueryParameters(utm), value, currency: pixel.currency }, { event_id: eventId });
    } else {
      if (pixel.conversionLabel.trim().length === 0) continue;
      ensureGtagBase(pixel.pixelId);
      if (!installedPixels.has(installKey)) window.gtag?.('config', pixel.pixelId);
      window.gtag?.('event', 'conversion', {
        ...trackingQueryParameters(utm),
        send_to: `${pixel.pixelId}/${pixel.conversionLabel}`,
        value,
        currency: pixel.currency,
        transaction_id: saleId,
      });
    }

    installedPixels.add(installKey);
    markFired(key, true);
  }
}

function installMetaPixels(checkout: PublicCheckout, utm?: CheckoutUtmAttribution): void {
  const pixels = pixelsByProvider(checkout, 'facebook');
  if (pixels.length === 0) {
    return;
  }
  ensureMetaBase();
  for (const pixel of pixels) {
    const key = `facebook:${pixel.pixelId}`;
    if (installedPixels.has(key)) {
      continue;
    }
    installedPixels.add(key);
    window.fbq?.('init', pixel.pixelId);
  }
  const loadKey = `checkout:${checkout.checkoutId}:meta-load`;
  if (alreadyFired(loadKey)) {
    return;
  }
  markFired(loadKey);
  if (utm) window.fbq?.('track', 'PageView', trackingQueryParameters(utm));
  else window.fbq?.('track', 'PageView');
  window.fbq?.('track', 'InitiateCheckout', checkoutEventData(checkout, utm), { eventID: `checkout:${checkout.checkoutId}:InitiateCheckout` });
}

function installTikTokPixels(checkout: PublicCheckout, utm?: CheckoutUtmAttribution): void {
  const pixels = pixelsByProvider(checkout, 'tiktok');
  if (pixels.length === 0) {
    return;
  }
  ensureTikTokBase();
  for (const pixel of pixels) {
    const key = `tiktok:${pixel.pixelId}`;
    if (installedPixels.has(key)) {
      continue;
    }
    installedPixels.add(key);
    window.ttq?.load?.(pixel.pixelId);
  }
  const loadKey = `checkout:${checkout.checkoutId}:tiktok-load`;
  if (alreadyFired(loadKey)) {
    return;
  }
  markFired(loadKey);
  window.ttq?.page?.();
  window.ttq?.track?.('ViewContent', checkoutEventData(checkout, utm), { event_id: `checkout:${checkout.checkoutId}:ViewContent` });
  window.ttq?.track?.('InitiateCheckout', checkoutEventData(checkout, utm), { event_id: `checkout:${checkout.checkoutId}:InitiateCheckout` });
}

function installGooglePixels(checkout: PublicCheckout, utm?: CheckoutUtmAttribution): void {
  const pixels = pixelsByProvider(checkout, 'google_ads');
  const firstPixel = pixels[0];
  if (firstPixel === undefined) {
    return;
  }
  ensureGtagBase(firstPixel.pixelId);
  for (const pixel of pixels) {
    const key = `google_ads:${pixel.pixelId}`;
    if (installedPixels.has(key)) {
      continue;
    }
    installedPixels.add(key);
    window.gtag?.('config', pixel.pixelId);
  }
  for (const pixel of pixels) {
    if (pixel.conversionEvent !== 'begin_checkout') {
      continue;
    }
    const key = `checkout:${checkout.checkoutId}:begin_checkout:${pixel.pixelId}`;
    if (alreadyFired(key)) {
      continue;
    }
    markFired(key);
    window.gtag?.('event', 'conversion', { ...trackingQueryParameters(utm), send_to: googleSendTo(pixel) });
  }
}

function ensureMetaBase(): void {
  if (window.fbq !== undefined) {
    return;
  }
  const stub = function (this: unknown): void {
    if (stub.callMethod !== undefined) {
      stub.callMethod.apply(stub, Array.prototype.slice.call(arguments) as unknown[]);
    } else {
      stub.queue.push(arguments);
    }
  } as MetaPixelStub;
  stub.push = stub;
  stub.loaded = true;
  stub.version = '2.0';
  stub.queue = [];
  window.fbq = stub;
  if (window._fbq === undefined) {
    window._fbq = stub;
  }
  appendScript('https://connect.facebook.net/en_US/fbevents.js');
}

function ensureTikTokBase(): void {
  if (window.ttq !== undefined) {
    return;
  }
  window.TiktokAnalyticsObject = 'ttq';
  const ttq = [] as unknown[][] as TikTokQueue;
  ttq.methods = [...TIKTOK_METHODS];
  ttq.setAndDefer = (target, method) => {
    target[method] = (...args: unknown[]) => {
      (target as unknown as unknown[][]).push([method, ...args]);
    };
  };
  for (const method of TIKTOK_METHODS) {
    ttq.setAndDefer(ttq as unknown as Record<string, unknown>, method);
  }
  ttq.instance = (pixelId) => {
    const instanceQueue = (ttq._i?.[pixelId] ?? []) as unknown as Record<string, unknown>;
    for (const method of TIKTOK_METHODS) {
      ttq.setAndDefer?.(instanceQueue, method);
    }
    return instanceQueue;
  };
  ttq.load = (pixelId, options) => {
    const entry = [] as unknown[] & { _u?: string };
    entry._u = TIKTOK_EVENTS_SRC;
    ttq._i = ttq._i ?? {};
    ttq._i[pixelId] = entry;
    ttq._t = ttq._t ?? {};
    ttq._t[pixelId] = Date.now();
    ttq._o = ttq._o ?? {};
    ttq._o[pixelId] = options ?? {};
    appendScript(`${TIKTOK_EVENTS_SRC}?sdkid=${encodeURIComponent(pixelId)}&lib=ttq`);
  };
  window.ttq = ttq;
}

function ensureGtagBase(firstTagId: string): void {
  if (window.gtag !== undefined) {
    return;
  }
  window.dataLayer = window.dataLayer ?? [];
  const gtag = function (this: unknown): void {
    window.dataLayer?.push(arguments);
  };
  window.gtag = gtag;
  appendScript(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(firstTagId)}`);
  window.gtag('js', new Date());
}

function googleSendTo(pixel: PublicCheckoutTrackingPixel): string {
  const label = pixel.conversionLabel.trim();
  return label.length > 0 ? `${pixel.pixelId}/${label}` : pixel.pixelId;
}

function pixelsByProvider(
  checkout: PublicCheckout,
  provider: PublicCheckoutTrackingPixel['provider'],
): PublicCheckoutTrackingPixel[] {
  return (checkout.tracking?.pixels ?? []).filter(
    (pixel) => pixel.provider === provider && pixel.pixelId.trim().length > 0,
  );
}

function alreadyFired(key: string, persistent = false): boolean {
  if (firedEvents.has(key)) {
    return true;
  }
  if (!persistent) {
    return false;
  }
  try {
    if (window.sessionStorage.getItem(`${SESSION_PREFIX}${key}`) !== null) {
      firedEvents.add(key);
      return true;
    }
  } catch {
    return false;
  }
  return false;
}

function markFired(key: string, persistent = false): void {
  firedEvents.add(key);
  if (!persistent) {
    return;
  }
  try {
    window.sessionStorage.setItem(`${SESSION_PREFIX}${key}`, '1');
  } catch {
    return;
  }
}

function appendScript(src: string): void {
  if (document.querySelector(`script[src="${src}"]`) !== null) {
    return;
  }
  const script = document.createElement('script');
  const loaded = new Promise<void>((resolve) => {
    script.addEventListener('load', () => resolve(), { once: true });
    script.addEventListener('error', () => resolve(), { once: true });
  });
  loadingScripts.add(loaded);
  void loaded.then(() => loadingScripts.delete(loaded));
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
}
